// frontend/src/pages/LoginPage/LoginPage.jsx

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { Form, Input, Button, Card, Typography, Alert, Image } from 'antd';
import { GoogleLoginButton, hasGoogleOAuth } from '../../components/GoogleLoginButton/GoogleLoginButton';
import ReenviarConfirmacao from '../../components/ReenviarConfirmacao/ReenviarConfirmacao';
import { MailOutlined, LockOutlined } from '@ant-design/icons';
import logo from '../../assets/images/logo_vertical.jpg';
// 1. IMPORTAÇÃO DO TRATAMENTO DE ERRO
import { getErrorMessage } from '../../utils/errorHandler';
import { irAposLogin } from '../../utils/legalAccess';
import { obterOrigemCadastro } from '../../utils/origemCadastro';
import {
  buildAuthQuery,
  isPerfilPrestadorParam,
  setPrestadorIntent,
} from '../../utils/prestadorIntentStorage';
import '../../styles/auth.css';

const { Title } = Typography;

// --- CONFIGURAÇÃO VISUAL DE VERSÃO ---
const SYSTEM_VERSION = 'v1.4.1';

function LoginPage() {
  const [error, setError] = useState('');
  // E-mail com cadastro ainda não confirmado (senha certa): mostra o reenvio do link.
  const [emailNaoConfirmado, setEmailNaoConfirmado] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get('ref') || '';
  const perfilPrestador = isPerfilPrestadorParam(searchParams.get('perfil'));

  useEffect(() => {
    if (perfilPrestador) setPrestadorIntent();
  }, [perfilPrestador]);

  // Retoma um destino interno após o login (ex.: /convite/:token).
  useEffect(() => {
    const redirect = searchParams.get('redirect');
    if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) {
      try { sessionStorage.setItem('redirect_apos_login', redirect); } catch { /* ignore */ }
    }
  }, [searchParams]);

  // Veio da confirmação de e-mail (link já usado / sem sessão): já preenche o e-mail.
  useEffect(() => {
    const email = location.state?.email;
    if (email) form.setFieldsValue({ email });
  }, [location.state, form]);

  // --- CORREÇÃO DO LOOP DE LOGIN ---
  // Ao montar o componente de Login, garantimos que qualquer sessão anterior
  // ou tokens expirados sejam limpos. Isso evita que o sistema tente usar
  // headers antigos na requisição de login.
  useEffect(() => {
    if (auth.isAuthenticated) {
        // Se já estiver autenticado (e válido), redireciona.
        // Mas se caiu aqui por erro, o auth.logout() abaixo garante a limpeza.
        // Para segurança, vamos forçar o logout visual ao entrar na tela de Login.
        auth.logout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); 

  const onFinish = async (values) => {
    setError('');
    setEmailNaoConfirmado('');
    setLoading(true);
    try {
      const userData = await auth.login(values.email, values.password);
      irAposLogin(userData, navigate);
    } catch (err) {
      // 2. USO NO LOGIN
      // Se for "Credenciais inválidas", o getErrorMessage vai retornar ele mesmo se for string simples,
      // ou a mensagem padrão se for um erro de rede.
      if (err.codigo === 'email_nao_verificado') {
          setEmailNaoConfirmado(values.email.trim());
      } else if (err.message === "Credenciais inválidas") {
          setError('E-mail ou senha inválidos. Tente novamente.');
      } else {
          setError(getErrorMessage(err, 'Não foi possível conectar. Verifique sua rede ou tente mais tarde.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (tokenResponse) => {
    setError('');
    setLoadingGoogle(true);
    try {
      // Se a conta for criada agora pelo Google, o backend grava esta origem;
      // no login de quem já tem conta ela é ignorada.
      const userData = await auth.loginWithGoogle(
        tokenResponse.access_token,
        ref || undefined,
        obterOrigemCadastro(searchParams),
      );
      irAposLogin(userData, navigate);
    } catch (err) {
      setError(getErrorMessage(err, 'Não foi possível entrar com o Google. Tente novamente.'));
    } finally {
      setLoadingGoogle(false);
    }
  };

  const handleGoogleError = () => {
    setError('Login com Google cancelado ou falhou.');
    setLoadingGoogle(false);
  };

  return (
    <div className="auth-shell">
      <Card className="auth-card">
        <Image
          width={180}
          src={logo}
          alt="Logo Saldo Ativo"
          preview={false}
          className="auth-logo"
        />
        
        <Title level={3} className="auth-title">
          Acesse sua conta
        </Title>
        <p className="auth-subtitle">Entre para acompanhar suas finanças e milhas</p>
        
        {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />}

        {emailNaoConfirmado && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 24, textAlign: 'left' }}
            message="Falta confirmar o seu e-mail"
            description={
              <>
                <div style={{ marginBottom: 12 }}>
                  Sua senha está certa, mas o cadastro ainda não foi ativado. Clique no link que
                  enviamos para <strong>{emailNaoConfirmado}</strong> (veja também Spam e Promoções).
                </div>
                <ReenviarConfirmacao email={emailNaoConfirmado} />
              </>
            }
          />
        )}
        
        <Form
          form={form}
          name="login"
          initialValues={{ remember: true }}
          onFinish={onFinish}
          autoComplete="off"
          size="large"
        >
          <Form.Item
            name="email"
            rules={[{ required: true, message: 'Por favor, insira seu e-mail!' }]}
          >
            <Input prefix={<MailOutlined />} placeholder="Email" />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: 'Por favor, insira sua senha!' }]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Senha" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              Entrar
            </Button>

            {hasGoogleOAuth && (
              <div style={{ marginTop: 16 }}>
                <GoogleLoginButton
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  loading={loadingGoogle}
                  disabled={loading}
                  block
                  contexto="login"
                />
              </div>
            )}
            
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Link to={`/cadastro${buildAuthQuery({ ref, perfilPrestador })}`} style={{ fontSize: '0.9em', color: 'var(--primary-color)' }}>
                Criar conta
              </Link>
              <Link to="/esqueci-senha" style={{ fontSize: '0.9em', color: 'var(--primary-color)' }}>
                Esqueci minha senha
              </Link>
            </div>
          </Form.Item>
        </Form>

        <div className="auth-footer">
            <Typography.Text type="secondary">
                Sistema Saldo Ativo &copy; 2026 <br/>
                Versão: <strong>{SYSTEM_VERSION}</strong>
            </Typography.Text>
        </div>

      </Card>
    </div>
  );
}

export default LoginPage;